// SPDX-License-Identifier: Apache-2.0

// SlackThreadCard — specialised tool-card for `slack.*` results.
//
// Iris's design rule: progressive enhancement. If the result schema
// doesn't match what we expect, fall back to a tiny placeholder rather
// than crash the chat-panel render.
//
// Channel-type colour stripe: public → cyan, private → violet, DM → yellow.
// Mrkdwn rewrite is best-effort (see notes in SlackThreadCard's body).

import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import CommsSnapshotButton from "./CommsSnapshotButton";
import "./comms.css";

// ── Types — match the shape of korotovsky/slack-mcp-server payloads ──

export interface SlackUser {
  id?: string;
  name?: string;
  real_name?: string;
  is_bot?: boolean;
  image?: string;
}

export interface SlackReaction {
  name: string;
  count: number;
  users?: string[];
}

export interface SlackMessage {
  ts?: string;
  user?: string;
  username?: string;
  user_profile?: SlackUser;
  text?: string;
  thread_ts?: string;
  reply_count?: number;
  reactions?: SlackReaction[];
  bot_id?: string;
  type?: string;
}

export interface SlackChannelInfo {
  id?: string;
  name?: string;
  /** "public_channel" | "private_channel" | "im" | "mpim" — korotovsky's vocabulary. */
  type?: string;
  is_private?: boolean;
  is_im?: boolean;
}

export interface SlackToolResult {
  display_hint?: "slack-thread";
  channel?: SlackChannelInfo | string;
  channel_name?: string;
  messages?: SlackMessage[];
  // Some endpoints return a single message:
  message?: SlackMessage;
  // List endpoints return arrays of these instead:
  channels?: Array<SlackChannelInfo & { topic?: string }>;
  users?: Array<SlackUser & { email?: string }>;
}

interface Props {
  /** The raw tool result. May be JSON, may be a string blob. */
  result: unknown;
  /** Tool name e.g. "slack.read_channel" — used for the head label + snapshot title. */
  toolName: string;
  /** When true, the snapshot button is shown in the foot. Defaults to true. */
  showSnapshot?: boolean;
}

// ── Channel-class helpers ─────────────────────────────────────────────

type ChannelClass = "public" | "private" | "dm";

function classifyChannel(info: SlackChannelInfo | string | undefined): ChannelClass {
  if (typeof info === "string") return "public";
  if (!info) return "public";
  if (info.is_im || info.type === "im" || info.type === "mpim") return "dm";
  if (info.is_private || info.type === "private_channel") return "private";
  return "public";
}

function channelLabel(info: SlackChannelInfo | string | undefined, fallback?: string): string {
  if (typeof info === "string") return info.startsWith("#") ? info : `#${info}`;
  if (!info) return fallback ? `#${fallback}` : "#channel";
  if (info.is_im || info.type === "im") return info.name ? `@${info.name}` : "DM";
  return info.name ? `#${info.name}` : info.id ?? "#channel";
}

// ── Mrkdwn → minimal HTML ────────────────────────────────────────────
// Best-effort: bold, italic, code, links. Channel/user/special mentions
// rendered as inline chips. Newlines preserved.
//
// Subtleties:
//  - Slack mrkdwn `*bold*` (single-asterisk) is NOT markdown's `*emphasis*`;
//    we treat single `*…*` as bold per Slack's spec.
//  - Underscore `_italic_` matches Slack and markdown both.
//  - Strikethrough `~text~` is Slack-specific (single tilde) — implemented.
//  - `<url|label>` is Slack's link form, NOT markdown's `[label](url)`.
//  - Lists, blockquotes, headings, tables: NOT supported. Slack mrkdwn
//    doesn't have headings/tables anyway. Blockquotes (`>` line prefix)
//    and bullet lists (`• `) render as plain lines — gracefully degrades.
//  - User mentions `<@U123>` → chip @U123 (we don't have the user table
//    here; rendering the raw id is honest).
//  - Channel mentions `<#C123|name>` → chip #name.
//  - Special mentions `<!channel>`, `<!here>`, `<!everyone>` → chip @label.
//  - `&amp;`, `&lt;`, `&gt;` decoded last so we don't re-decode link payloads.
//
// What FAILS gracefully: nested formatting (`*_a_*`) — we apply each pass
// linearly, so the inner one usually wins; if both were structurally
// important the user can click "remember this" and edit in the vault.

function rewriteSlackText(raw: string): JSX.Element[] {
  // Tokenise on the angle-bracket forms first so they're left intact for
  // chip rendering. Everything else passes through formatInline.
  const out: JSX.Element[] = [];
  const re = /<([@#!])([^>|]+)(?:\|([^>]+))?>/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > lastIdx) {
      out.push(formatInline(raw.slice(lastIdx, m.index), `t${key++}`));
    }
    const [, sigil, body, label] = m;
    out.push(renderChip(sigil ?? "@", body ?? "", label, `c${key++}`));
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < raw.length) {
    out.push(formatInline(raw.slice(lastIdx), `t${key++}`));
  }
  return out;
}

function renderChip(
  sigil: string,
  body: string,
  label: string | undefined,
  key: string,
): JSX.Element {
  if (sigil === "#") {
    return (
      <span class="mv-slack__chip mv-slack__chip--channel" data-key={key}>
        #{label ?? body}
      </span>
    );
  }
  if (sigil === "!") {
    return (
      <span class="mv-slack__chip mv-slack__chip--special" data-key={key}>
        @{label ?? body}
      </span>
    );
  }
  // `@` user
  return (
    <span class="mv-slack__chip" data-key={key}>
      @{label ?? body}
    </span>
  );
}

// Inline markdown-ish formatting for a span of plain text. Returns a
// Fragment carrying <strong>/<em>/<code>/<a>. We split on tokens, never
// build an HTML string from raw input — XSS-safe by construction.
function formatInline(raw: string, key: string): JSX.Element {
  // Decode the three Slack-mandated entity escapes.
  const decoded = raw
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

  // Single regex captures fenced code, inline code, bold (single *), italic
  // (underscore), strike (single ~), and bare URLs. Order matters; longest
  // first so `*foo*` doesn't get eaten by italic.
  const tokenRe = /(```[\s\S]*?```)|(`[^`]+`)|(\*[^*\n]+\*)|(_[^_\n]+_)|(~[^~\n]+~)|(https?:\/\/[^\s)<]+)/g;
  const parts: JSX.Element[] = [];
  let i = 0;
  let idx = 0;
  let mm: RegExpExecArray | null;
  while ((mm = tokenRe.exec(decoded)) !== null) {
    if (mm.index > i) parts.push(<span data-k={`p${idx++}`}>{decoded.slice(i, mm.index)}</span>);
    const tok = mm[0];
    if (tok.startsWith("```")) {
      parts.push(<code data-k={`p${idx++}`}>{tok.slice(3, -3)}</code>);
    } else if (tok.startsWith("`")) {
      parts.push(<code data-k={`p${idx++}`}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith("*")) {
      parts.push(<strong data-k={`p${idx++}`}>{tok.slice(1, -1)}</strong>);
    } else if (tok.startsWith("_")) {
      parts.push(<em data-k={`p${idx++}`}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith("~")) {
      parts.push(
        <span data-k={`p${idx++}`} style={{ "text-decoration": "line-through" }}>
          {tok.slice(1, -1)}
        </span>,
      );
    } else if (tok.startsWith("http")) {
      parts.push(
        <a data-k={`p${idx++}`} href={tok} target="_blank" rel="noopener noreferrer">
          {tok}
        </a>,
      );
    }
    i = mm.index + tok.length;
  }
  if (i < decoded.length) parts.push(<span data-k={`p${idx++}`}>{decoded.slice(i)}</span>);
  return <span data-key={key}>{parts}</span>;
}

// ── Avatar / user helpers ─────────────────────────────────────────────

function userInitials(m: SlackMessage): string {
  const name = m.user_profile?.real_name ?? m.user_profile?.name ?? m.username ?? m.user ?? "?";
  const tokens = name.replace(/[^a-zA-Z\s]/g, "").trim().split(/\s+/);
  const first = tokens[0]?.[0] ?? name[0] ?? "?";
  const second = tokens[1]?.[0] ?? "";
  return (first + second).toUpperCase().slice(0, 2);
}

function userDisplay(m: SlackMessage): string {
  return (
    m.user_profile?.real_name ??
    m.user_profile?.name ??
    m.username ??
    m.user ??
    (m.bot_id ? "Bot" : "Unknown")
  );
}

function tsToHuman(ts?: string): string {
  if (!ts) return "";
  const sec = Number(ts.split(".")[0]);
  if (!Number.isFinite(sec) || sec <= 0) return ts;
  const d = new Date(sec * 1000);
  // Short form: HH:MM if today, else MMM-DD HH:MM.
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (sameDay) return `${hh}:${mm}`;
  const month = d.toLocaleString("en", { month: "short" });
  const day = String(d.getDate()).padStart(2, "0");
  return `${month}-${day} ${hh}:${mm}`;
}

// ── Result-shape coercion (defensive parse) ──────────────────────────

function parseResult(raw: unknown): SlackToolResult | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as SlackToolResult;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw as SlackToolResult;
  return null;
}

// ── Component ─────────────────────────────────────────────────────────

export default function SlackThreadCard(props: Props) {
  const parsed = createMemo(() => parseResult(props.result));

  const channelClass = createMemo(() => classifyChannel(parsed()?.channel));

  const messages = createMemo<SlackMessage[]>(() => {
    const p = parsed();
    if (!p) return [];
    if (Array.isArray(p.messages)) return p.messages;
    if (p.message) return [p.message];
    return [];
  });

  const headerLabel = createMemo(() =>
    channelLabel(parsed()?.channel, parsed()?.channel_name),
  );

  // Snapshot payload — formatted markdown of the rendered thread.
  const snapshotMarkdown = createMemo(() => {
    const ms = messages();
    if (ms.length === 0) return "";
    const lines: string[] = [];
    for (const m of ms) {
      const who = userDisplay(m);
      const when = tsToHuman(m.ts);
      lines.push(`**${who}** _(${when})_`);
      if (m.text) lines.push(m.text);
      if (m.reactions && m.reactions.length > 0) {
        lines.push(
          m.reactions.map((r) => `:${r.name}: ${r.count}`).join("  "),
        );
      }
      lines.push("");
    }
    return lines.join("\n");
  });

  const snapshotSlug = createMemo(() => {
    const label = headerLabel();
    return label.replace(/^[#@]/, "").replace(/[^a-zA-Z0-9-]+/g, "-").slice(0, 40) || "slack";
  });

  // Sensitivity per Iris's research: DMs/private → private, public → internal.
  const defaultSensitivity = createMemo<"private" | "internal">(() =>
    channelClass() === "public" ? "internal" : "private",
  );

  // Fallback render — never crash, just show the raw blob in a neutral pre.
  const fallback = (note: string) => (
    <div class="mv-comms mv-slack mv-slack--public">
      <div class="mv-comms__head">
        <span class="mv-comms__source mv-comms__source--slack">
          <span class="mv-comms__source-glyph">#</span>
          slack
        </span>
        <span class="mv-comms__title">{props.toolName}</span>
      </div>
      <div class="mv-comms__empty">{note}</div>
    </div>
  );

  return (
    <Show when={parsed()} fallback={fallback("loading…")}>
      <Show when={messages().length > 0} fallback={fallback("no messages")}>
        <div
          class={`mv-comms mv-slack mv-slack--${channelClass()}`}
          data-tool-name={props.toolName}
        >
          <div class="mv-comms__head">
            <span
              class={`mv-comms__source mv-comms__source--slack mv-comms__source--${channelClass()}`}
            >
              <span class="mv-comms__source-glyph">#</span>
              slack
            </span>
            <span class="mv-comms__title">{headerLabel()}</span>
            <span class="mv-comms__meta">
              {messages().length} msg{messages().length === 1 ? "" : "s"}
            </span>
          </div>

          <div class="mv-comms__body">
            <For each={messages()}>
              {(m) => {
                const isThreaded = !!m.thread_ts && m.thread_ts !== m.ts;
                const isBot = !!m.bot_id;
                return (
                  <div
                    class={`mv-slack__msg${isThreaded ? " mv-slack__msg--threaded" : ""}`}
                  >
                    <div class={`mv-slack__avatar${isBot ? " mv-slack__avatar--bot" : ""}`}>
                      {userInitials(m)}
                    </div>
                    <div class="mv-slack__bubble">
                      <div class="mv-slack__head">
                        <span class="mv-slack__user">{userDisplay(m)}</span>
                        <span class="mv-slack__ts">{tsToHuman(m.ts)}</span>
                      </div>
                      <Show when={m.text}>
                        <div class="mv-slack__text">{rewriteSlackText(m.text ?? "")}</div>
                      </Show>
                      <Show when={m.reactions && m.reactions.length > 0}>
                        <div class="mv-slack__reactions">
                          <For each={m.reactions}>
                            {(r) => (
                              <span class="mv-slack__reaction">
                                <span>:{r.name}:</span>
                                <span>{r.count}</span>
                              </span>
                            )}
                          </For>
                        </div>
                      </Show>
                      <Show when={m.reply_count && m.reply_count > 0 && !isThreaded}>
                        <div class="mv-slack__thread-marker">
                          ↳ {m.reply_count} repl{m.reply_count === 1 ? "y" : "ies"}
                        </div>
                      </Show>
                    </div>
                  </div>
                );
              }}
            </For>
          </div>

          <Show when={props.showSnapshot !== false}>
            <div class="mv-comms__foot">
              <CommsSnapshotButton
                source="slack"
                title={`slack — ${headerLabel()}`}
                slug={snapshotSlug()}
                content={snapshotMarkdown()}
                location={`inbox/comms/slack/`}
                defaultSensitivity={defaultSensitivity()}
                tags={["comms", "slack"]}
              />
              <span class="mv-comms-snapshot__where">
                → ~/brain/inbox/comms/slack/
              </span>
            </div>
          </Show>
        </div>
      </Show>
    </Show>
  );
}
